import { Form } from 'antd';
import capitalize from 'lodash/capitalize';
import React, { memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';

import BackOfficeDrawer from 'components/drawers/BackOfficeDrawer';
import BackOfficeEnterpriseForm from 'components/forms/BackOfficeEnterpriseForm';
import BackOfficeEnterpriseLoadingGate from 'components/loadingGates/BackOfficeEnterpriseLoadingGate';
import WarningModal from 'components/modals/WarningModal';
import BackOfficeEnterpriseRequest from 'components/requests/BackOfficeEnterpriseRequest';
import { useBackOfficeEnterprisesRequestParameters } from 'components/requests/useBackOfficeRequestParameters';
import { SEARCH_PARAMS } from 'constants/search-params';
import useBackOfficeEnterprise from 'store/modules/backOfficeEnterprise/hooks';
import { editBackOfficeEnterprise, loadBackOfficeEnterprises } from 'store/modules/backOfficeEnterprises/actions';
import ActionTypes from 'store/modules/backOfficeEnterprises/constants';
import clearErrors from 'store/modules/error/actions';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import { EnterpriseStatus } from 'types/models/enterprise';
import { supportsCibo as supportsCiboFunc } from 'utils/integration/supports-cibo';

const actions = [ActionTypes.EDIT_BACK_OFFICE_ENTERPRISE_REQUEST];

function BackOfficeEnterpriseEditDrawer(): JSX.Element | null {
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const enterpriseId = Number(searchParams.get(SEARCH_PARAMS.BACK_OFFICE.SELECTED_ENTERPRISE)) || undefined;

  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  const visible = !!enterpriseId;
  const backOfficeEnterpriseData = useBackOfficeEnterprise();
  let billingAddressId: number | undefined;
  let supportsCibo = false;
  if (backOfficeEnterpriseData) {
    billingAddressId = backOfficeEnterpriseData.billingAddressId;
    supportsCibo = supportsCiboFunc(backOfficeEnterpriseData.billingAddress?.country);
  }

  const dispatch = useDispatch();
  const requestParameters = useBackOfficeEnterprisesRequestParameters();

  useEffect(() => {
    if (visible) dispatch(clearErrors(actions));
  }, [dispatch, visible]);

  const handleClose = () => {
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.delete(SEARCH_PARAMS.BACK_OFFICE.SELECTED_ENTERPRISE);
      return newSearchParams;
    });
  };

  const onEditSuccess = () => {
    handleClose();
    dispatch(loadBackOfficeEnterprises(requestParameters));
  };

  const submitChangesAndCloseDrawer = () => {
    form
      .validateFields()
      .then((values) => {
        if (enterpriseId) {
          dispatch(
            editBackOfficeEnterprise(enterpriseId, {
              ...values,
              billingAddressId
            }, onEditSuccess)
          );
        }
      })
      .catch(() => {
        // Ignoring promise rejection
      });
  };

  const initiateSave = () => {
    const status = form.getFieldValue('status');
    const isDestructive = [EnterpriseStatus.DISABLED, EnterpriseStatus.DUPLICATE].includes(status);
    if (isDestructive) {
      setIsConfirmModalVisible(true);
    } else {
      submitChangesAndCloseDrawer();
    }
  };

  const hasPermission = useMyUserHasInternalPermission({
    resource: ProtectedResource.ENTERPRISE,
    action: ResourceAction.UPDATE
  });

  return (
    <BackOfficeDrawer
      visible={visible}
      backOfficeSection={hasPermission ? 'Edit Enterprise' : 'Enterprise Details'}
      actions={hasPermission ? actions : []}
      handleSave={hasPermission ? initiateSave : undefined}
      handleClose={handleClose}
      noFooter={!hasPermission}
    >
      <BackOfficeEnterpriseRequest enterpriseId={enterpriseId} />
      <BackOfficeEnterpriseLoadingGate enterpriseId={enterpriseId}>
        <>
          <BackOfficeEnterpriseForm
            actions={hasPermission ? actions : []}
            form={form}
            initialValues={backOfficeEnterpriseData || undefined}
            readOnly={!hasPermission}
          />
          {/* @see: https://www.figma.com/design/Uk1cflnASlDT4APqhIwu21/Cibo-Lite-Pasture-Zones?node-id=45-6190&t=tsnnvAMKZgYAUM3u-4 */}
          {supportsCibo && (
            <div className="vertical-gap-16">
              <div className="section-heading">Cibo lite Pasture Zones</div>
              <Link to={`${enterpriseId}/cibo-subscriptions`}>
                {hasPermission ? 'Manage' : 'View'} Subscriptions
              </Link>
            </div>
          )}
        </>
      </BackOfficeEnterpriseLoadingGate>

      <WarningModal
        isOpen={isConfirmModalVisible}
        title={`Are you sure you want to update this enterprise and set its status to ${capitalize(form.getFieldValue('status'))}?`}
        onDismiss={() => setIsConfirmModalVisible(false)}
        onConfirm={() => {
          setIsConfirmModalVisible(false);
          submitChangesAndCloseDrawer();
        }}
        confirmText="Update"
      >
        <div className="body-copy">
          This action is irreversible and will immediately unlink all users and invalidate all outstanding invitations
          from this enterprise. As a result, users won't be able to access it anymore.
        </div>
      </WarningModal>
    </BackOfficeDrawer>
  );
}

export default memo(BackOfficeEnterpriseEditDrawer);
